import { defineStore } from 'pinia'
import { computed, inject, ref } from 'vue'
import type {
    IAssignedSubAccountData,
    IAssignedSubAccountSimple,
    IUpdateSubAccountAddressPayload,
} from '@/typings/subAccount'
import { useToast } from 'primevue'
import { errorHandlerKey, servicesKey } from '@/config/keys.ts'
import i18n from '@/i18n'
import { onApiErrorHandler } from '@/helpers/backendErrors.ts'
import { DocumentToAcceptError } from '@/models/DocumentToAcceptError.ts'
import { CHILD_PROTECT_DOCUMENT, RODO_DOCUMENT } from '@/config/requiredDocumentsCodes.ts'
import { useRouter } from 'vue-router'
import { routeNames } from '@/router/names.ts'

export const useSubAccountStore = defineStore('subAccount', () => {
    const toast = useToast()
    const router = useRouter()

    const services = inject(servicesKey)
    const errorHandler = inject(errorHandlerKey)

    const subAccountsOptions = ref<IAssignedSubAccountSimple[]>([])
    const selectedSubAccountDetails = ref<null | IAssignedSubAccountData>(null)

    const isSubAccountActive = computed(() => Boolean(selectedSubAccountDetails.value?.isActive))

    const sendMessageIsActive = computed(() =>
        Boolean(selectedSubAccountDetails.value?.sendMessageIsActive),
    )

    const clearSubAccountData = (): void => {
        subAccountsOptions.value = []
        selectedSubAccountDetails.value = null
    }

    const clearSelectedSubAccountDetails = (): void => {
        selectedSubAccountDetails.value = null
    }

    const loadSubAccountsOptions = async (): Promise<{
        isSuccess: boolean
        documentToAccept: typeof RODO_DOCUMENT | typeof CHILD_PROTECT_DOCUMENT | null
    }> => {
        try {
            const data = await services!.subAccount.getAssignedSubAccounts()

            if (!data.length) {
                throw new Error('[Invalid backend response] Empty assigned sub accounts list')
            }

            subAccountsOptions.value = data

            return { isSuccess: true, documentToAccept: null }
        } catch (err) {
            onApiErrorHandler({
                err,
                toast,
                errorTitle: i18n.global.t('Error'),
                defaultErrorMessage: i18n.global.t(
                    'There was a problem downloading the list of people under your care',
                ),
                action: 'loadSubAccountsOptions',
                errorHandler,
            })
            subAccountsOptions.value = []

            const requiredDoc = err instanceof DocumentToAcceptError ? err.documentToAccept : null

            return { isSuccess: false, documentToAccept: requiredDoc }
        }
    }

    const fetchSubAccountDetailsById = async (
        subAccountId: string,
    ): Promise<IAssignedSubAccountData | null> => {
        try {
            const data = await services!.subAccount.getSubAccountDataById(subAccountId)

            if (!data) {
                throw new Error('Missing data from getSubAccountDataById')
            }

            if (!data.rodo) {
                await router.push({
                    name: routeNames.RODO_SUB_ACCOUNT_VIEW_NAME,
                    params: {
                        subAccountId: data.subaccountId,
                    },
                })
            }

            selectedSubAccountDetails.value = data

            return data
        } catch (err) {
            selectedSubAccountDetails.value = null

            onApiErrorHandler({
                err,
                toast,
                errorTitle: i18n.global.t('Error'),
                defaultErrorMessage: i18n.global.t('There was a problem downloading data'),
                action: 'fetchSubAccountDetails',
                errorHandler,
            })

            return null
        }
    }

    const updateSubAccountAddress = async (
        subAccountId: string,
        payload: IUpdateSubAccountAddressPayload,
    ): Promise<boolean> => {
        const isSuccess = await services!.subAccount.updateSubAccountAddress(subAccountId, payload)

        if (isSuccess) {
            await fetchSubAccountDetailsById(subAccountId)
        }

        return isSuccess
    }

    return {
        subAccountsOptions,
        selectedSubAccountDetails,
        isSubAccountActive,
        sendMessageIsActive,
        loadSubAccountsOptions,
        clearSubAccountData,
        fetchSubAccountDetailsById,
        updateSubAccountAddress,
        clearSelectedSubAccountDetails,
    }
})
